import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Container, Alert, ProgressBar } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

let NavigationBar;
let LoadingSpinner;
let GuaranteeList;
let GuaranteeDetail;
let GuaranteeForm;
let Documents;

try {
  NavigationBar = require('./components/common/NavigationBar').default;
  LoadingSpinner = require('./components/common/LoadingSpinner').default;
  GuaranteeList = require('./components/guarantees/GuaranteeList').default;
  GuaranteeDetail = require('./components/guarantees/GuaranteeDetail').default;
  GuaranteeForm = require('./components/guarantees/GuaranteeForm').default;
  Documents = require('./components/documents/Documents').default;
} catch (err) {
  console.error("Error importing components:", err);
  NavigationBar = () => <div className="navbar bg-primary text-white p-2">Mobility App Navigation</div>;
  LoadingSpinner = () => <div className="text-center p-5">Loading...</div>;
  GuaranteeList = () => <div className="p-3">Guarantee List Component Error</div>;
  GuaranteeDetail = () => <div className="p-3">Guarantee Detail Component Error</div>;
  GuaranteeForm = () => <div className="p-3">Guarantee Form Component Error</div>;
  Documents = () => <div className="p-3">Documents Component Error</div>;
}


// Determine the env
const isDevelopmentMode = process.env.REACT_APP_ENVIRONMENT === 'dev' || 
                          process.env.NODE_ENV === 'development';
const isTestMode = process.env.REACT_APP_ENVIRONMENT === 'test';
              

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasToken, setHasToken] = useState(false);
  const [error, setError] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Determine development mode
  const inDevMode = isDevelopmentMode;
  
  // Handle redirect to login page with SIMPLIFIED return URL
  useEffect(() => {
    if (isRedirecting && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else   if (isRedirecting && redirectCountdown === 0) {
      const returnToUrl = encodeURIComponent(process.env.REACT_APP_FRONTEND_URL);
      window.location.href = `${process.env.REACT_APP_SSO_LOGIN_URL}?returnTo=${returnToUrl}`;
    }
  }, [isRedirecting, redirectCountdown]);
  
  useEffect(() => {
    try {
      console.log("App mounting - checking for token");
      console.log("Environment:", inDevMode ? "Development" : (isTestMode ? "Test" : "Production"));
      // console.log(process.env.REACT_APP_ENVIRONMENT);
      console.log("Current URL:", window.location.href);
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('jwt');
      const storedToken = localStorage.getItem('mobilityToken');
      
      console.log("URL token exists:", !!urlToken);
      console.log("Stored token exists:", !!storedToken);
      
      if (urlToken) {
        localStorage.setItem('mobilityToken', urlToken);
        setHasToken(true);
        
        // Remove the token from URL to prevent sharing/bookmarking issues
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('jwt');
          window.history.replaceState({}, document.title, url.toString());
        }
      } 
      else if (storedToken) {
        setHasToken(true);
      } 
      else if (inDevMode) {
        // For development, create a mock token
        const mockToken = "==MOCK==";
        localStorage.setItem('mobilityToken', mockToken);
        setHasToken(true);
        console.log("Development mode active - using mock token");  
      }
    } catch (err) {
      console.error("Error in token initialization:", err);
      setError("Failed to initialize authentication: " + err.message);
    } finally {
      // finish loading regardless of outcome
      setTimeout(() => {
        setIsLoading(false);
        console.log("App finished loading");
      }, 500);
    }
  }, [inDevMode]);
  
  // If still loading, show a spinner
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // If error occurred, show error
  if (error) {
    return (
      <Container className="text-center py-5">
        <div className="alert alert-danger">
          <h2>Application Error</h2>
          <p>{error}</p>
        </div>
      </Container>
    );
  }
  
  if (!hasToken) {
    if (!isRedirecting) {
      setIsRedirecting(true);
    }
    
    return (
      <Container className="text-center py-5">
        <Alert variant="warning">
          <h2>Authentication Required</h2>
          <p>
            Kein gültiges Authentifizierungs-Token gefunden. Bitte greifen Sie auf diese Anwendung über das ATP-Hauptsystem zu.
          </p>
          <div className="mt-4">
            <p>Sie werden in <strong>{redirectCountdown}</strong> Sekunden zum Login weitergeleitet...</p>
            <ProgressBar 
              now={(5 - redirectCountdown) * 20} 
              variant="info" 
              className="mt-2" 
              animated 
            />
          </div>
        </Alert>
      </Container>
    );
  }
  
  // Main application with error boundary
  return (
    <ErrorBoundary>
      <Router>
        <div className="app-container">
          <NavigationBar />
          <Container className="my-4 main-content">
            <Routes>
              <Route path="/" element={<Navigate to="/guarantees" />} />
              <Route path="/guarantees" element={<GuaranteeList />} />
              <Route path="/guarantees/:confirmationCode" element={<GuaranteeDetail />} />
              <Route path="/guarantees/new" element={<GuaranteeForm />} />
              <Route path="/guarantees/edit/:confirmationCode" element={<GuaranteeForm />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="*" element={<Navigate to="/guarantees" />} />
            </Routes>
          </Container>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

// Simple error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("React error caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container className="py-5">
          <div className="alert alert-danger">
            <h2>Application Error</h2>
            <p>Something went wrong with the application.</p>
            <details className="mt-3">
              <summary>Error Details</summary>
              <pre>{this.state.error && this.state.error.toString()}</pre>
            </details>
          </div>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default App;